import React, { useEffect, useState, useCallback } from 'react';
import { Select } from 'antd';
import { F921, LeaveBalance, ProxyEmployee, p922aApi } from '../../../services/p922aApi';
import { dateToInput, inputToDate, getApprovalStatus, autoCorrectTime, fmt } from '../helpers';
import LookupField from '../../../components/LookupField';

interface Tab1Props {
  formData: Partial<F921>;
  names: Record<string, string>;
  isEditing: boolean;
  getField: (key: keyof F921) => F921[keyof F921];
  setField: (key: keyof F921, value: F921[keyof F921]) => void;
  resolveSingleName: (nameKey: string, type: string, code: string) => void;
  isFieldDisabled: (key: string) => boolean;
  getFieldError: (field: string) => string | undefined;
}

const Tab1BasicInfo: React.FC<Tab1Props> = React.memo(({
  formData, names, isEditing, getField, setField, resolveSingleName, isFieldDisabled, getFieldError,
}) => {
  const statusInfo = getApprovalStatus(formData as { f921_30?: string });

  // ── Proxy list (F905) for Select/combobox ──
  const [proxyList, setProxyList] = useState<ProxyEmployee[]>([]);

  const loadProxyList = useCallback(async (empCode: string) => {
    if (!empCode) { setProxyList([]); return; }
    const list = await p922aApi.getProxyList(empCode);
    setProxyList(list);
  }, []);

  useEffect(() => {
    const empCode = String(formData.f921_02 || '');
    if (empCode) loadProxyList(empCode);
  }, [formData.f921_02, loadProxyList]);

  // ── Leave balance (F9131) ──
  const [leaveBalances, setLeaveBalances] = useState<LeaveBalance[]>([]);

  const loadLeaveBalance = useCallback(async (empCode: string) => {
    if (!empCode) { setLeaveBalances([]); return; }
    const balances = await p922aApi.getLeaveBalance(empCode);
    setLeaveBalances(balances);
  }, []);

  useEffect(() => {
    const empCode = String(formData.f921_02 || '');
    if (empCode) loadLeaveBalance(empCode);
  }, [formData.f921_02, loadLeaveBalance]);

  // ── Leave type change handler (BR-V09: auto-update unit) ──
  const handleLeaveTypeBlur = useCallback(async (code: string) => {
    resolveSingleName('leaveType', 'leaveType', code);
    if (code.trim()) {
      const info = await p922aApi.getLeaveTypeInfo(code.trim());
      if (info.unit) {
        setField('f921_18', info.unit);
      }
    }
  }, [resolveSingleName, setField]);

  // ── Time auto-correction (BR-C05) ──
  const handleTimeBlur = useCallback((field: keyof F921) => {
    const raw = String(getField(field) || '');
    const corrected = autoCorrectTime(raw);
    if (corrected !== raw) {
      setField(field, corrected);
    }
  }, [getField, setField]);

  return (
    <div className="tab-content-inner">
      {/* Section: 基本欄位 */}
      <div className="section-title">請假資料</div>
      <div className="form-grid form-grid-4col">
        {/* 員工代號 */}
        <label>員工代號</label>
        <div className="lookup-field">
          <input
            value={String(getField('f921_02') || '')}
            onChange={e => setField('f921_02', e.target.value)}
            onBlur={e => {
              const code = e.target.value;
              resolveSingleName('employee', 'employee', code);
              resolveSingleName('department', 'department', '');
              loadProxyList(code);
              loadLeaveBalance(code);
            }}
            disabled={isFieldDisabled('f921_02')}
            maxLength={8}
            className={getFieldError('f921_02') ? 'field-error' : ''}
          />
          <span className="lookup-name">{names.employee || ''}</span>
        </div>

        {/* 部門代號 */}
        <label>部門代號</label>
        <div className="lookup-field">
          <input
            value={String(getField('f921_31') || '')}
            disabled
            readOnly
          />
          <span className="lookup-name">{names.department || ''}</span>
        </div>

        {/* 申請日期 */}
        <label>申請日期</label>
        <div>
          <input
            type="date"
            value={dateToInput(getField('f921_11') as string | null)}
            onChange={e => setField('f921_11', inputToDate(e.target.value))}
            disabled={isFieldDisabled('f921_11')}
          />
        </div>

        {/* 假別代號 — LookupField with unit auto-update */}
        <label>假別代號</label>
        <div className="lookup-field">
          <LookupField
            lookupType="leaveType"
            value={String(getField('f921_12') || '')}
            onChange={(code) => {
              setField('f921_12', code);
              handleLeaveTypeBlur(code);
            }}
            disabled={isFieldDisabled('f921_12')}
            style={{ width: 80 }}
            title="搜尋假別"
          />
          <span className="lookup-name">{names.leaveType || ''}</span>
        </div>

        {/* 開始日 */}
        <label>開始日</label>
        <div>
          <input
            type="date"
            value={dateToInput(getField('f921_13') as string | null)}
            onChange={e => setField('f921_13', inputToDate(e.target.value))}
            disabled={isFieldDisabled('f921_13')}
            className={getFieldError('f921_13') ? 'field-error' : ''}
          />
        </div>

        {/* 開始時間 — with BR-C05 auto-correction */}
        <label>時間（開始）</label>
        <div>
          <input
            value={String(getField('f921_14') || '')}
            onChange={e => setField('f921_14', e.target.value)}
            onBlur={() => handleTimeBlur('f921_14')}
            disabled={isFieldDisabled('f921_14')}
            maxLength={5}
            placeholder="HH:MM"
            className={getFieldError('f921_14') ? 'field-error' : ''}
          />
        </div>

        {/* 結束日 */}
        <label>結束日</label>
        <div>
          <input
            type="date"
            value={dateToInput(getField('f921_15') as string | null)}
            onChange={e => setField('f921_15', inputToDate(e.target.value))}
            disabled={isFieldDisabled('f921_15')}
            className={getFieldError('f921_15') ? 'field-error' : ''}
          />
        </div>

        {/* 結束時間 — with BR-C05 auto-correction */}
        <label>時間（結束）</label>
        <div>
          <input
            value={String(getField('f921_16') || '')}
            onChange={e => setField('f921_16', e.target.value)}
            onBlur={() => handleTimeBlur('f921_16')}
            disabled={isFieldDisabled('f921_16')}
            maxLength={5}
            placeholder="HH:MM"
            className={getFieldError('f921_16') ? 'field-error' : ''}
          />
        </div>

        {/* 代理人 — Select/combobox from F905 proxy list (BR-F12) */}
        <label>代理人</label>
        <div className="lookup-field">
          <Select
            value={String(getField('f921_10') || '') || undefined}
            onChange={(val: string) => {
              setField('f921_10', val);
              resolveSingleName('agent', 'employee', val);
            }}
            disabled={isFieldDisabled('f921_10')}
            style={{ minWidth: 120 }}
            placeholder="-- 請選擇 --"
            allowClear
            options={proxyList.map(p => ({
              value: p.code,
              label: `${p.code}${p.name ? ` (${p.name})` : ''}`,
            }))}
          />
          <span className="lookup-name">{names.agent || ''}</span>
        </div>

        {/* 總請假數 */}
        <label>總請假數</label>
        <div className="inline-pair">
          <input
            value={String(getField('f921_19') || 0)}
            disabled
            readOnly
            style={{ width: 80, textAlign: 'right' }}
          />
          <span className="unit-label">{String(getField('f921_18') || '')}</span>
        </div>
      </div>

      {/* 事由 — full width */}
      <div className="form-grid form-grid-2col" style={{ marginTop: 12 }}>
        <label>請假事由</label>
        <div>
          <textarea
            value={String(getField('f921_21') || '')}
            onChange={e => setField('f921_21', e.target.value)}
            disabled={isFieldDisabled('f921_21')}
            rows={3}
            maxLength={254}
            style={{ width: '100%' }}
          />
        </div>
      </div>

      {/* Section: 假別餘額 (F9131 Grid) */}
      {leaveBalances.length > 0 && (
        <>
          <div className="section-title" style={{ marginTop: 16 }}>假別餘額</div>
          <div className="detail-grid-wrapper" style={{ maxHeight: 200, overflow: 'auto' }}>
            <table className="detail-grid">
              <thead>
                <tr>
                  <th style={{ width: 65 }}>員工編號</th>
                  <th style={{ width: 70 }}>年度</th>
                  <th style={{ width: 72 }}>假別</th>
                  <th style={{ width: 54 }}>可用數</th>
                  <th style={{ width: 177 }}>已用(核准數)</th>
                </tr>
              </thead>
              <tbody>
                {leaveBalances.map((b, i) => (
                  <tr key={i}>
                    <td>{b.f9131_01}</td>
                    <td>{b.f9131_02}</td>
                    <td>{b.f9131_03}</td>
                    <td style={{ textAlign: 'right' }}>{fmt(b.f9131_11)}</td>
                    <td style={{ textAlign: 'right' }}>{fmt(b.f9131_14)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* Section: 審核資訊 */}
      <div className="section-title" style={{ marginTop: 16 }}>審核資訊</div>
      <div className="form-grid form-grid-4col">
        {/* 審核狀態 */}
        <label>審核狀態</label>
        <div>
          {statusInfo && (
            <span className={`badge ${statusInfo.cls}`}>{statusInfo.text}</span>
          )}
        </div>

        {/* 須複核原因 */}
        <label>須複核原因</label>
        <div>
          <input value={String(getField('f921_27') || '')} disabled readOnly />
        </div>

        {/* 入單人員 */}
        <label>入單人員</label>
        <div className="lookup-field">
          <input value={String(getField('f921_91') || '')} disabled readOnly />
          <span className="lookup-name">{names.entryPerson || ''}</span>
        </div>

        {/* 入單日 */}
        <label>入單日</label>
        <div>
          <input
            type="date"
            value={dateToInput(getField('f921_92') as string | null)}
            disabled
            readOnly
          />
        </div>

        {/* 初核人員 */}
        <label>初核人員</label>
        <div className="lookup-field">
          <input value={String(getField('f921_22') || '')} disabled readOnly />
          <span className="lookup-name">{names.reviewer || ''}</span>
        </div>

        {/* 初核日 */}
        <label>初核日</label>
        <div>
          <input
            type="date"
            value={dateToInput(getField('f921_23') as string | null)}
            disabled
            readOnly
          />
        </div>

        {/* 審核人 */}
        <label>審核</label>
        <div className="lookup-field">
          <input value={String(getField('f921_72') || '')} disabled readOnly />
          <span className="lookup-name">{names.auditor || ''}</span>
        </div>

        {/* 核准日 (was mislabeled as 審核日) */}
        <label>核准日</label>
        <div>
          <input
            type="date"
            value={dateToInput(getField('f921_73') as string | null)}
            disabled
            readOnly
          />
        </div>

        {/* 人事查核人 */}
        <label>人事查核</label>
        <div className="lookup-field">
          <input value={String(getField('f921_67') || '')} disabled readOnly />
          <span className="lookup-name">{names.hrChecker || ''}</span>
        </div>

        {/* 人事查核日 */}
        <label>查核日</label>
        <div>
          <input
            type="date"
            value={dateToInput(getField('f921_68') as string | null)}
            disabled
            readOnly
          />
        </div>

        {/* 複核人員 */}
        <label>複核人員</label>
        <div className="lookup-field">
          <input value={String(getField('f921_25') || '')} disabled readOnly />
          <span className="lookup-name">{names.approver || ''}</span>
        </div>

        {/* 複核日 */}
        <label>複核日</label>
        <div>
          <input
            type="date"
            value={dateToInput(getField('f921_26') as string | null)}
            disabled
            readOnly
          />
        </div>
      </div>
    </div>
  );
});

Tab1BasicInfo.displayName = 'Tab1BasicInfo';
export default Tab1BasicInfo;
